import * as React from 'react';
import { ComboBox, Item, Flex, View, Text } from '@adobe/react-spectrum';
import { useAsyncList } from '@react-stately/data';
import type { Key } from '@react-types/shared';
import { IFilterControl, IFilterItem } from '../../FilterSetupPlaceholder';
import { useDependency } from '../../../../../../../contexts/DependencyProvider';
import { ListFPARequest2 } from '../../../../../../../services/soap/project/requests/ListFPARequest2';
import { useFilterGuard } from '../../../../../../../hooks/useFilterGuard';
import styles from './ProjectFilterControl.module.css';

/**
 * Props for the ProjectFilterControl component
 */
export interface IProjectFilterControlProps {
  /** Filter control configuration object */
  filter: IFilterControl;
  /** Callback function called when filter values change */
  onFilterChange: (filter: IFilterControl) => void;
}

interface Project {
  id: number;
  name: string;
}

export const ProjectFilterControl = React.forwardRef<HTMLDivElement, IProjectFilterControlProps>(
  (props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const { projectService, store } = useDependency();
    const apiDebounceRef = React.useRef<NodeJS.Timeout | null>(null);

    const currentFilterItem = React.useMemo(
      () =>
        filter.filterInfo[0] || {
          name: 'value',
          type: 'equals',
          value: '',
        },
      [filter.filterInfo]
    );

    const [selectedKey, setSelectedKey] = React.useState<Key | null>(
      currentFilterItem.value ? String(currentFilterItem.value) : null
    );
    const [inputValue, setInputValue] = React.useState<string>(
      currentFilterItem.valueLabel || ''
    );

    React.useEffect(() => {
      syncGuard(filter);
    }, [filter, syncGuard]);

    // Sync with filter changes from parent
    React.useEffect(() => {
      const currentItem = filter.filterInfo[0];
      if (currentItem) {
        setSelectedKey(currentItem.value ? String(currentItem.value) : null);
        setInputValue(currentItem.valueLabel || '');
      }
    }, [filter.filterInfo]);

    const list = useAsyncList<Project>({
      async load({ cursor, filterText }) {
        // Don't search if filterText is empty or too short
        if (!filterText || filterText.trim().length < 2) {
          return { items: [], cursor: undefined };
        }

        // Clear any existing debounce timeout
        if (apiDebounceRef.current) {
          clearTimeout(apiDebounceRef.current);
        }

        // Debounce API calls by 500ms
        return new Promise((resolve) => {
          apiDebounceRef.current = setTimeout(async () => {
            try {
              const offset = cursor ? Number(cursor) : 0;
              const limit = 50;

              const response = await projectService.listFPA2(
                new ListFPARequest2(store.Server, store.SessionId, {
                  limit: limit,
                  offset: offset,
                  flatView: true,
                  fulltext: filterText + '*',
                  ORDER: {
                    COL: [
                      {
                        name: 'name',
                        desc: false as any,
                      },
                      {
                        name: 'created',
                        desc: true as any,
                      },
                    ],
                  },
                })
              );

              // Extract only PROJECT items from response
              const projects: Project[] =
                response?.ITEMS?.map((entry: any) => entry.ITEM?.PROJECT).filter((p: Project) => p) || [];

              // Map to required format
              const mappedProjects: Project[] = projects.map((project: any) => ({
                id: project.id,
                name: project.name || String(project.id),
              }));

              resolve({
                items: mappedProjects,
                cursor: mappedProjects.length === limit ? String(offset + limit) : undefined,
              });
            } catch (error) {
              console.error('Error fetching projects:', error);
              resolve({ items: [], cursor: undefined });
            }
          }, 500);
        });
      },
    });

    const handleSelectionChange = React.useCallback(
      (key: Key | null) => {
        setSelectedKey(key);

        if (key === null) {
          // Clear the filter
          const updatedFilterInfo: IFilterItem[] = [
            {
              ...currentFilterItem,
              type: currentFilterItem.type || 'equals',
              value: '',
              valueLabel: '',
            },
          ];
          notifyChange({
            ...filter,
            filterInfo: updatedFilterInfo,
          });
          setInputValue('');
        } else {
          // Find selected project
          const selectedProject = list.items.find((p) => String(p.id) === String(key));
          if (selectedProject) {
            const updatedFilterInfo: IFilterItem[] = [
              {
                ...currentFilterItem,
                type: currentFilterItem.type || 'equals',
                value: String(selectedProject.id),
                valueLabel: selectedProject.name,
              },
            ];
            notifyChange({
              ...filter,
              filterInfo: updatedFilterInfo,
            });
            setInputValue(selectedProject.name);
          }
        }
      },
      [filter, currentFilterItem, notifyChange, list.items]
    );

    const handleInputChange = React.useCallback(
      (value: string) => {
        setInputValue(value);
        // Clear selection if input is cleared
        if (!value) {
          setSelectedKey(null);
          const updatedFilterInfo: IFilterItem[] = [
            {
              ...currentFilterItem,
              type: currentFilterItem.type || 'equals',
              value: '',
              valueLabel: '',
            },
          ];
          notifyChange({
            ...filter,
            filterInfo: updatedFilterInfo,
          });
        }
        list.setFilterText(value);
      },
      [filter, currentFilterItem, notifyChange, list]
    );

    // Cleanup debounce on unmount
    React.useEffect(() => {
      return () => {
        if (apiDebounceRef.current) {
          clearTimeout(apiDebounceRef.current);
        }
      };
    }, []);

    return (
      <div ref={forwardedRef} className={styles.projectFilterControl}>
        <Flex direction="column" gap="size-200">
          {/* Label row */}
          <Text UNSAFE_className={styles.label}>{filter.label || filter.name}</Text>
          
          {/* ComboBox row */}
          <View width="100%">
            <ComboBox
              aria-label="Project search"
              items={list.items}
              selectedKey={selectedKey}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              onSelectionChange={handleSelectionChange}
              loadingState={list.loadingState}
              onLoadMore={list.loadMore}
              allowsCustomValue={false}
              width="100%"
              description="Type to search projects..."
            >
              {(item) => (
                <Item key={String(item.id)} textValue={item.name}>
                  {item.name}
                </Item>
              )}
            </ComboBox>
          </View>
        </Flex>
      </div>
    );
  }
);

ProjectFilterControl.displayName = 'ProjectFilterControl';
